import { useId, type ReactNode } from 'react'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'

import { FieldShell } from '@/components/form/field-shell'
import { Input } from '@/components/ui/input'

/**
 * A text field that lays out what is typed and can answer a question when it is left.
 *
 * The shared `TextField` binds straight through to the form value, which is right for a name and
 * wrong for a Ghana Card number: those are read off a card as a run of digits and belong on screen
 * in their punctuated form. Formatting happens on the way in and never refuses a keystroke, so
 * backspacing through a half-typed number always works.
 *
 * Anything rendered as a child sits under the control, above the validation message. That is
 * where a duplicate warning goes: it is information about the value, not a reason to reject it.
 */
export interface FormattedTextFieldProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label: string
  description?: string
  required?: boolean
  className?: string
  placeholder?: string
  autoComplete?: string
  inputMode?: 'text' | 'numeric' | 'tel' | 'email'
  /** Rewrites the value as it is typed. */
  format?: (raw: string) => string
  /** Called when the control is left, with the value as it then stands. */
  onValueBlur?: (value: string) => void
  children?: ReactNode
}

export function FormattedTextField<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  required,
  className,
  placeholder,
  autoComplete = 'off',
  inputMode,
  format,
  onValueBlur,
  children,
}: FormattedTextFieldProps<TFieldValues>) {
  const id = useId()
  const { field, fieldState } = useController({ control, name })
  const error = fieldState.error?.message
  const value = typeof field.value === 'string' ? field.value : ''

  return (
    <FieldShell
      id={id}
      label={label}
      description={description}
      required={required}
      error={error}
      className={className}
    >
      <Input
        id={id}
        name={field.name}
        ref={field.ref}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        spellCheck={false}
        onChange={(event) => {
          field.onChange(format ? format(event.target.value) : event.target.value)
        }}
        onBlur={(event) => {
          field.onBlur()
          onValueBlur?.(event.target.value)
        }}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : description ? `${id}-description` : undefined}
      />
      {children}
    </FieldShell>
  )
}
